'use client'

import { useState, useCallback, useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import {
  MessageCircle,
  ChevronUp,
  ChevronDown,
  PenSquare,
  Search,
  Maximize2,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getConversations } from '@/lib/messaging/conversation-actions'
import type { ConversationListItem } from '@/lib/messaging/conversation-actions'
import type { UserSearchResult } from '@/lib/search/types'
import { DefaultAvatar } from '@/components/ui/default-avatar'
import { useSseChannel } from '@/lib/sse/use-sse-channel'
import { relativeShort } from '@/lib/messaging/format-time'
import { useHydrated } from '@/hooks/useHydrated'
import { DockChatWindow } from '@/app/(app)/messages/_components/dock-chat-window'
import { NewMessageDialog } from '@/app/(app)/messages/_components/new-message-dialog'

interface MessagesDockProps {
  userId: string
}

/** Max simultaneously-docked chat windows (LinkedIn/Messenger-style row). */
const MAX_OPEN_CHATS = 3

interface OpenChat {
  id: string
  conv: ConversationListItem
  minimized: boolean
}

/**
 * LinkedIn-style messaging surface: a persistent "Messaging" panel docked to
 * the bottom-right corner (header + search + conversation list, collapsible),
 * with conversations opening as a row of mini-chat windows to its left.
 */
export function MessagesDock({ userId }: MessagesDockProps) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [newOpen, setNewOpen] = useState(false)
  const [openChats, setOpenChats] = useState<OpenChat[]>([])
  const queryClient = useQueryClient()
  const hydrated = useHydrated()
  const router = useRouter()

  const conversationsQuery = useQuery({
    queryKey: ['messages-dock', 'conversations'] as const,
    queryFn: async (): Promise<ConversationListItem[]> => {
      const result = await getConversations({ limit: 15 })
      if (!result.success) throw new Error(result.error)
      return result.data ?? []
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
  const conversations = (conversationsQuery.data ?? []) as ConversationListItem[]
  const isLoading = conversationsQuery.isLoading
  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0)

  const onSseMessage = useCallback(
    (event: { type: string }) => {
      if (event.type !== 'message') return
      queryClient.invalidateQueries({ queryKey: ['messages-dock', 'conversations'] })
    },
    [queryClient],
  )
  useSseChannel(`messages:user:${userId}`, onSseMessage, { enabled: !!userId })

  const openChat = useCallback(
    (conv: ConversationListItem) => {
      // Docked mini-chat is a desktop pattern; on mobile open the full page.
      const isDesktop =
        typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
      if (!isDesktop) {
        router.push(`/messages/${conv.id}`)
        return
      }
      setOpenChats((prev) => {
        if (prev.some((c) => c.id === conv.id)) {
          return prev.map((c) =>
            c.id === conv.id ? { ...c, conv, minimized: false } : c,
          )
        }
        const next = [...prev, { id: conv.id, conv, minimized: false }]
        return next.length > MAX_OPEN_CHATS ? next.slice(next.length - MAX_OPEN_CHATS) : next
      })
    },
    [router],
  )

  const closeChat = useCallback((id: string) => {
    setOpenChats((prev) => prev.filter((c) => c.id !== id))
  }, [])

  const toggleMinimize = useCallback((id: string) => {
    setOpenChats((prev) =>
      prev.map((c) => (c.id === id ? { ...c, minimized: !c.minimized } : c)),
    )
  }, [])

  // Compose → open the new conversation as a mini-chat window in place.
  const openChatFromUser = useCallback(
    (conversationId: string, user: UserSearchResult) => {
      openChat({
        id: conversationId,
        other_user: {
          id: user.id,
          username: user.username,
          display_name: user.displayName,
          avatar_url: user.avatarUrl,
        },
        last_message_at: null,
        last_message_preview: null,
        last_message_is_self: false,
        unread_count: 0,
        is_muted: false,
      })
      queryClient.invalidateQueries({ queryKey: ['messages-dock', 'conversations'] })
    },
    [openChat, queryClient],
  )

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return conversations
    return conversations.filter((c) => {
      const name = (c.other_user.display_name ?? c.other_user.username ?? '').toLowerCase()
      const preview = (c.last_message_preview ?? '').toLowerCase()
      return name.includes(q) || (c.other_user.username ?? '').toLowerCase().includes(q) || preview.includes(q)
    })
  }, [conversations, filter])

  return (
    // Desktop-only dock (LinkedIn-style). On mobile (<md) it's hidden: a
    // 340px fixed panel covers ~90% of a 375px viewport and sits on top of
    // bottom CTAs. Mobile users reach messaging via the /messages route
    // (bottom tab bar).
    <div className="fixed bottom-0 right-6 z-30 hidden items-end gap-3 md:flex">
      {openChats.map(({ id, conv, minimized }) => (
        <DockChatWindow
          key={id}
          conversation={conv}
          currentUserId={userId}
          minimized={minimized}
          onToggleMinimize={() => toggleMinimize(id)}
          onClose={() => closeChat(id)}
        />
      ))}

      {/* Persistent "Messaging" panel */}
      <div className="flex w-[340px] flex-col overflow-hidden rounded-t-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-[var(--shadow-lg)]">
        <div className="flex items-center gap-1 border-b border-[var(--color-border-default)] px-3 py-2.5">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <MessageCircle className="h-5 w-5 shrink-0 text-[var(--color-text-primary)]" aria-hidden="true" />
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">Messaging</span>
            {totalUnread > 0 && (
              <span
                className="inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white"
                style={{ background: 'var(--color-brand-indigo)' }}
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </button>
          <Link
            href="/messages"
            aria-label="Open Messages full page"
            title="Open full"
            className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
          >
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            aria-label="New message"
            title="New message"
            className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
          >
            <PenSquare className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Collapse messaging' : 'Expand messaging'}
            className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
        </div>

        {open && (
          <>
            <div className="border-b border-[var(--color-border-default)] px-3 py-2">
              <div className="flex items-center gap-2 rounded-full border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-1.5 focus-within:border-[var(--color-brand-indigo)]">
                <Search className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-muted)]" aria-hidden="true" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search messages"
                  aria-label="Search messages"
                  className="w-full bg-transparent text-sm text-[var(--color-text-primary)] outline-none focus-visible:outline-none! placeholder:text-[var(--color-text-muted)]"
                />
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-6 text-center text-xs text-[var(--color-text-muted)]">Loading…</div>
              ) : conversationsQuery.isError && conversations.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-xs text-[var(--color-text-muted)]">
                  <span>Couldn&apos;t load messages.</span>
                  <button
                    type="button"
                    onClick={() => conversationsQuery.refetch()}
                    className="font-medium text-[var(--color-brand-indigo)] hover:underline"
                  >
                    Try again
                  </button>
                </div>
              ) : conversations.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-4 py-8 text-center text-xs text-[var(--color-text-muted)]">
                  <span>No conversations yet.</span>
                  <button
                    type="button"
                    onClick={() => setNewOpen(true)}
                    className="rounded-md bg-[var(--color-brand-indigo)] px-3 py-1.5 font-semibold text-white"
                  >
                    New message
                  </button>
                </div>
              ) : visible.length === 0 ? (
                <p className="px-4 py-8 text-center text-xs text-[var(--color-text-muted)]">
                  No conversations match “{filter.trim()}”.
                </p>
              ) : (
                visible.map((c) => {
                  const name = c.other_user.display_name ?? c.other_user.username ?? 'User'
                  const isUnread = c.unread_count > 0
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => openChat(c)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[var(--color-bg-elevated)]"
                    >
                      <div className="relative shrink-0">
                        {c.other_user.avatar_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={c.other_user.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                        ) : (
                          <DefaultAvatar name={name} size={36} />
                        )}
                        {isUnread && (
                          <span
                            aria-hidden="true"
                            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[var(--color-bg-surface)]"
                            style={{ background: 'var(--color-brand-indigo)' }}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            className={`truncate text-sm text-[var(--color-text-primary)] ${isUnread ? 'font-bold' : 'font-medium'}`}
                          >
                            {name}
                          </span>
                          <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]" suppressHydrationWarning>
                            {hydrated ? relativeShort(c.last_message_at) : ''}
                          </span>
                        </div>
                        <p
                          className={`truncate text-xs ${isUnread ? 'font-semibold text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'}`}
                        >
                          {c.last_message_is_self && <span className="text-[var(--color-text-muted)]">You: </span>}
                          {c.last_message_preview ?? 'No messages yet'}
                        </p>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </>
        )}
      </div>

      <NewMessageDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onOpenConversation={openChatFromUser}
      />
    </div>
  )
}
